import { useState, useEffect, useRef, useCallback } from 'react';
import { playAlarmSound, playTestSound, stopAllAudio } from '../utils/audioUtils';
import { saveToStorage, loadFromStorage, STORAGE_KEYS } from '../utils/localStorage';

export function useAlarm(currentTime) {
  // 加载闹铃设置
  const loadSettings = () => {
    const defaults = {
      alarmHour: '07',
      alarmMinute: '00',
      snoozeMinutes: '05',
      selectedMelody: 'CLASSIC',
      growing: false,
      volume: 70
    };
    
    return loadFromStorage(STORAGE_KEYS.ALARM_SETTINGS, defaults);
  };

  // 加载闹铃激活状态
  const loadActiveState = () => {
    const defaults = { isActive: false, activatedAt: null };
    return loadFromStorage(STORAGE_KEYS.ALARM_ACTIVE_STATE, defaults);
  };

  const savedSettings = loadSettings();
  const savedActiveState = loadActiveState();

  const [alarmHour, setAlarmHour] = useState(savedSettings.alarmHour);
  const [alarmMinute, setAlarmMinute] = useState(savedSettings.alarmMinute);
  const [snoozeMinutes, setSnoozeMinutes] = useState(savedSettings.snoozeMinutes);
  const [selectedMelody, setSelectedMelody] = useState(savedSettings.selectedMelody);
  const [growing, setGrowing] = useState(savedSettings.growing);
  const [volume, setVolume] = useState(savedSettings.volume ?? 70);
  const [alarmActive, setAlarmActive] = useState(savedActiveState.isActive);
  const [alarmRinging, setAlarmRinging] = useState(false);
  const [timeLeft, setTimeLeft] = useState('');

  const audioControlRef = useRef(null);
  const timeSettingsRef = useRef({ alarmHour: savedSettings.alarmHour, alarmMinute: savedSettings.alarmMinute });
  const isSnoozingRef = useRef(false);

  // 保存激活状态到 localStorage
  const saveActiveState = useCallback((isActive) => {
    const activeState = {
      isActive,
      activatedAt: isActive ? Date.now() : null
    };
    
    saveToStorage(STORAGE_KEYS.ALARM_ACTIVE_STATE, activeState);
  }, []);

  // 自定义的 setAlarmActive 函数，同时保存状态
  const setAlarmActiveWithSave = useCallback((isActive) => {
    setAlarmActive(isActive);
    saveActiveState(isActive);
  }, [saveActiveState]);

  // 简化的保存逻辑
  const isInitialMount = useRef(true);
  // 保存设置的逻辑
  useEffect(() => {
    // 跳过初始挂载时的保存
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    
    const settings = {
      alarmHour,
      alarmMinute,
      snoozeMinutes,
      selectedMelody,
      growing,
      volume
    };
    
    saveToStorage(STORAGE_KEYS.ALARM_SETTINGS, settings);
  }, [alarmHour, alarmMinute, snoozeMinutes, selectedMelody, growing, volume]);

  // 监听时间设置变化，重置激活状态（但排除 snooze 操作）
  useEffect(() => {
    // 跳过初始挂载
    if (isInitialMount.current) {
      return;
    }
    
    // 如果是 snooze 操作，跳过重置
    if (isSnoozingRef.current) {
      isSnoozingRef.current = false;
      timeSettingsRef.current = { alarmHour, alarmMinute };
      return;
    }
    
    // 检查时间设置是否发生变化
    if (timeSettingsRef.current.alarmHour !== alarmHour || timeSettingsRef.current.alarmMinute !== alarmMinute) {
      console.log('⏰ 时间设置已更改，重置激活状态');
      setAlarmActiveWithSave(false);
      setAlarmRinging(false);
      
      // 停止当前音频
      if (audioControlRef.current) {
        audioControlRef.current.stop();
        audioControlRef.current = null;
      }
      stopAllAudio();
      
      // 更新引用
      timeSettingsRef.current = { alarmHour, alarmMinute };
    }
  }, [alarmHour, alarmMinute, setAlarmActiveWithSave]);

  const triggerAlarm = useCallback(() => {
    // 只在首页时触发普通闹钟
    if (typeof window !== 'undefined' && window.location.pathname !== '/') {
      console.log('⏰ [普通闹钟] 不在首页，跳过触发');
      return;
    }
    
    setAlarmRinging(true);
    // 使用选中的音效和音量设置
    playAlarmSound(selectedMelody, volume / 100, growing).then(audioControl => {
      audioControlRef.current = audioControl;
    }).catch(error => {
      console.error('闹钟音效播放失败:', error);
    });
  }, [selectedMelody, growing, volume]);

  // 初始化时检查激活状态和时间
  useEffect(() => {
    if (savedActiveState.isActive && savedActiveState.activatedAt) {
      console.log('🔄 检查激活的闹铃状态...');
      
      const now = new Date();
      const alarm = new Date();
      alarm.setHours(parseInt(alarmHour), parseInt(alarmMinute), 0, 0);
      
      // 如果闹铃时间已经过了，设置为明天
      if (alarm <= now) {
        alarm.setDate(alarm.getDate() + 1);
      }
      
      // 检查是否应该立即响铃（允许1分钟的误差）
      const timeDiff = now - alarm;
      if (timeDiff >= 0 && timeDiff <= 60000) { // 1分钟内
        console.log('⏰ 闹铃时间已到，立即触发');
        triggerAlarm();
      } else {
        console.log('✅ 闹铃仍在激活状态，继续倒计时');
      }
    }
  }, [triggerAlarm, savedActiveState.isActive, savedActiveState.activatedAt, alarmHour, alarmMinute]); // 添加依赖

  // Calculate time left until alarm
  useEffect(() => {
    if (alarmActive) {
      const now = new Date();
      const alarm = new Date();
      alarm.setHours(parseInt(alarmHour), parseInt(alarmMinute), 0, 0);
      
      if (alarm <= now) {
        alarm.setDate(alarm.getDate() + 1);
      }
      
      const diff = alarm - now;
      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);
      
      setTimeLeft(`${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`);
      
      // Check if alarm should ring
      if (hours === 0 && minutes === 0 && seconds === 0) {
        triggerAlarm();
      }
    }
  }, [currentTime, alarmActive, alarmHour, alarmMinute, triggerAlarm]);

  const stopAlarm = () => {
    setAlarmRinging(false);
    setAlarmActiveWithSave(false);
    
    // 停止当前播放的音频
    if (audioControlRef.current) {
      audioControlRef.current.stop();
      audioControlRef.current = null;
    }
    
    // 确保停止所有音频
    stopAllAudio();
  };

  const snoozeAlarm = () => {
    setAlarmRinging(false);
    
    // 停止当前音频
    if (audioControlRef.current) {
      audioControlRef.current.stop();
      audioControlRef.current = null;
    }
    stopAllAudio();
    
    // 计算新的闹钟时间
    const newMinute = (parseInt(alarmMinute) + parseInt(snoozeMinutes)) % 60;
    const newHour = (parseInt(alarmHour) + Math.floor((parseInt(alarmMinute) + parseInt(snoozeMinutes)) / 60)) % 24;
    const newMinuteStr = newMinute.toString().padStart(2, '0');
    const newHourStr = newHour.toString().padStart(2, '0');
    
    // 设置 snooze 标志，防止时间变化监听器重置激活状态
    isSnoozingRef.current = true;
    
    // 更新时间
    setAlarmMinute(newMinuteStr);
    setAlarmHour(newHourStr);
    
    // 确保闹钟保持激活状态并保存
    if (!alarmActive) {
      setAlarmActiveWithSave(true);
    } else {
      // 即使已经是激活状态，也重新保存以确保状态同步
      saveActiveState(true);
    }
    
    // 更新时间引用
    timeSettingsRef.current = { alarmHour: newHourStr, alarmMinute: newMinuteStr };
  };

  const testSound = () => {
    // 使用新的测试音效函数，播放当前选中的音效
    playTestSound(selectedMelody, 2000, volume / 100).then(audioControl => {
      console.log(`正在测试音效: ${selectedMelody}`);
    }).catch(error => {
      console.error('测试音效播放失败:', error);
    });
  };

  return {
    alarmHour,
    alarmMinute,
    snoozeMinutes,
    selectedMelody,
    growing,
    volume,
    alarmActive,
    alarmRinging,
    timeLeft,
    setAlarmHour,
    setAlarmMinute,
    setSnoozeMinutes,
    setSelectedMelody,
    setGrowing,
    setVolume,
    setAlarmActive: setAlarmActiveWithSave,
    stopAlarm,
    snoozeAlarm,
    testSound
  };
}